import { emptyChat, type Chat, type ChatMessage } from '../../mocks/chat';
import type { Project } from '../../api';

export type CenterTab = 'canvas' | 'content' | 'files' | 'artifacts' | 'review' | 'chat' | 'tests';
export type RightTab = 'pipeline' | 'agent' | 'analyze';

export const DOCKED_CHAT_KEY = 'sh_chat_docked';

export function parseChatDocked(raw: string | null): boolean {
  if (raw === 'fullscreen') return true;
  if (raw === 'docked') return false;
  return raw !== 'false';
}

export function chatDockedForCenter(center: CenterTab, docked: boolean): boolean {
  return center === 'chat' || docked;
}

export function parseRightTab(raw: string | null): RightTab {
  if (raw === 'agent' || raw === 'analyze') return raw;
  return 'pipeline';
}

export function rightTabForCenter(center: CenterTab, right: RightTab): RightTab {
  if (center === 'tests') return right === 'agent' ? 'agent' : 'analyze';
  return right === 'analyze' ? 'pipeline' : right;
}

export function analyzeChatKey(projectId: string | null | undefined): string {
  const id = (projectId || '').trim();
  return id ? `sh_analyze_chat:${id}` : 'sh_analyze_chat';
}

export function parseAnalyzeMessages(raw: string | null): ChatMessage[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as ChatMessage[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function loadAnalyzeMessages(projectId: string | null | undefined, storage: KV): ChatMessage[] {
  return parseAnalyzeMessages(storage.getItem(analyzeChatKey(projectId)));
}

export function saveAnalyzeMessages(projectId: string | null | undefined, messages: ChatMessage[], storage: KV): void {
  storage.setItem(analyzeChatKey(projectId), JSON.stringify(messages));
}

export type KV = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

export type StudioProjectState = { project?: Project };

export function chatStorageKey(projectId: string | null | undefined): string {
  const id = (projectId || '').trim();
  return id ? `sh_chats:${id}` : 'sh_chats';
}

export function activeChatKey(projectId: string | null | undefined): string {
  const id = (projectId || '').trim();
  return id ? `sh_active_chat:${id}` : 'sh_active_chat';
}

export function studioTabKey(projectId: string | null | undefined): string {
  const id = (projectId || '').trim();
  return id ? `sh_studio_tab:${id}` : 'sh_studio_tab';
}

export function projectIdFromSearch(search: string): string | null {
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('project');
}

export function projectFromNavState(state: unknown, projectId: string | null): Project | null {
  const project = (state as StudioProjectState | null | undefined)?.project;
  if (!project?.id || !projectId || project.id !== projectId) return null;
  return project;
}

export function parseCenterTab(raw: string | null): CenterTab {
  if (raw === 'chat' || raw === 'content' || raw === 'files' || raw === 'artifacts' || raw === 'review' || raw === 'canvas' || raw === 'tests') return raw;
  return 'canvas';
}

export function migrateCenterTab(centerRaw: string | null, rightRaw: string | null): CenterTab {
  if (rightRaw === 'tests') return 'tests';
  const tab = parseCenterTab(centerRaw);
  return tab === 'chat' ? 'canvas' : tab;
}

export function parseChatList(raw: string | null): Chat[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Chat[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function loadChats(projectId: string | null | undefined, storage: KV): Chat[] {
  const scoped = parseChatList(storage.getItem(chatStorageKey(projectId)));
  return scoped.length ? scoped : [emptyChat()];
}

export function seedBlankStudioSession(
  projectId: string,
  storage: { setItem(key: string, value: string): void } = localStorage,
): void {
  const chat = emptyChat();
  storage.setItem(studioTabKey(projectId), 'canvas');
  storage.setItem(chatStorageKey(projectId), JSON.stringify([chat]));
  storage.setItem(activeChatKey(projectId), chat.id);
}

export function saveChats(projectId: string | null | undefined, chats: Chat[], storage: KV): void {
  if (!chats.length) return;
  storage.setItem(chatStorageKey(projectId), JSON.stringify(chats));
}
